import { CategoryCodes, getActiveGroups } from '@olxeu-eprivacy-storage/js';
import { hydraConfig } from '../config/hydra';
import { getNinjaConfig } from '../config/ninja';
import { hasRequiredConsentFor } from '../consent';
import { Channel, ChannelName, HYDRA_HOST, TIMESTAMP_PROPERTY, Trackers, VISIBILITY_STATE } from '../const';
import { cleanCookie } from '../cookies/cleanCookie';
import { getDefaultParams as ninjaGetDefaultParams } from '../core/params';
import { trackWebVital } from '../core/utils';
import { collectCalls } from '../core/utils';
import { trackError, trackWithBeacon } from '../core/utils';
import { makeMapping, makeObjectMapping } from '../mapping';
import { deucex, getCurrentPath, getProtocol, objectToQueryString } from '../utils';
import { loadWebVitals } from '../web-vitals';

const TRACKER_KEY = 'H';

// Link event
let isLinkEvent = false;

// Internal vars
// let config = null;
// let finished = false;
// let iframeReady = true;
let initPushed = false;
let lastCurrentPage = null;
export let lastEventName = null;
export let lastTrackPage = null;

// Web vitals need the first page event
export let firstTrackPage = null;
export let currentPlatform;

// Internal function - Add Listener only one time
function init() {
  if (!initPushed) {
    // iframeReady = true;
    initPushed = true;
    lastCurrentPage = null;
    lastTrackPage = null;
    firstTrackPage = null;
    lastEventName = null;
  }
}

/**
 * Get list of Hydra-only default params
 * @param {Record<string, any>} params Tracking params
 * @returns {Record<string, any>}
 */
function getDefaultParams(params) {
  const defaultParams = {};

  // Session
  defaultParams.sl = params.sessionParams.sessionLong;
  defaultParams.s = params.sessionParams.session;
  defaultParams.cl = params.sessionParams.sessionCountLong;
  defaultParams.c = params.sessionParams.sessionCount;

  // Timestamp - should be generated when the event is added to the dataLayer
  defaultParams.t = params.customParams?.[TIMESTAMP_PROPERTY] ?? Date.now();

  // Add consent. Do not track "strictly necessary" category C0001
  const activeGroups = getActiveGroups().filter(group => group !== CategoryCodes.C0001);

  defaultParams.consent = activeGroups.join(',');

  // not yet default param, but will be
  defaultParams.as = getVisibilityState();

  // Discover some params from the url
  if (params.invite) {
    defaultParams.iv = params.invite;
  }

  // Host
  if (params.host) {
    defaultParams.host = params.host;
  }

  // Hash
  if (params.hash) {
    if (getNinjaConfig().siteUrl !== 'www.console.data.olx.org' && getNinjaConfig().siteUrl !== 'www.console-stg.data.olx.org') {
      defaultParams.hash = params.hash;
    }
  }

  // Referrer
  if (params.referrer) {
    defaultParams.referer = params.referrer;
  }

  // Default invite
  defaultParams.ivd = 'olx-' + hydraConfig.params.cC.toLowerCase() + '_organic';

  if (undefined !== hydraConfig.params.rE) {
    defaultParams.rE = hydraConfig.params.rE;
  }

  // Current page
  if (params.eventData.category) {
    // Add current pageName
    if (!params.pageName) {
      if (lastCurrentPage) {
        defaultParams.cP = lastCurrentPage;
      }
    }
  } else if (params.pageName) {
    lastCurrentPage = getPageName();
    defaultParams.cP = lastCurrentPage;
  }

  if (!getNinjaConfig().unitTest) {
    defaultParams.js = 1;
  }

  for (const key of Object.keys(defaultParams)) {
    const mapped = makeMapping(TRACKER_KEY, key, params.customParams[key]);

    if (mapped.key === false) {
      continue;
    }
  }
  // console.log('NINJA', defaultParams);
  return defaultParams;
}

/**
 * Adapt apps' logic to web.
 * The value is a number where 0 means that the app is in the foreground and it's active, 1 - the app is in the foreground but not active for any reason, and 2 - the app in the background and it's not visible to the user
 * @see https://console.data.olx.org/user-behaviour/all-products/motors/properties/motors.as/trackers
 */
function getVisibilityState() {
  // visible and active
  if (document.hasFocus()) {
    return VISIBILITY_STATE.ACTIVE;
  }

  // not visible, for whatever reason (on background or covered)
  if (document.hidden) {
    return VISIBILITY_STATE.HIDDEN;
  }

  // (partially) visible, but not on focus
  return VISIBILITY_STATE.INACTIVE_VISIBLE;
}

/**
 * Reads config for platform property and applies it to the already mapped params
 */
export function applyPlatform(mappedParams) {
  // fallback to a default prop name if the region config lacks specific definition
  let property = hydraConfig.platform_property || 'platform';

  // first we try to take the platform defined in getNinjaConfig()
  if (getNinjaConfig().platform === Channel.Desktop) {
    mappedParams[property] = ChannelName.Desktop;
    currentPlatform = ChannelName.Desktop;
  } else if (getNinjaConfig().platform === Channel.Mobile) {
    mappedParams[property] = ChannelName.Mobile;
    currentPlatform = ChannelName.Mobile;
  } else {
    // Check platform only if we know the field
    if (mappedParams[property]) {
      // Store the platform value if exists and is different
      if (mappedParams[property] !== currentPlatform) {
        currentPlatform = mappedParams[property];
      }
    } else if (currentPlatform) {
      // If there is no platform, use the last value found
      mappedParams[property] = currentPlatform;
    }
  }
  return mappedParams;
}

function getParams(params) {
  const eventData = [];
  const urlParams = {};
  let newValues;
  let customParams = getCustomParams(params); // mapping is inside
  const ninjaDefaultParams = makeObjectMapping(TRACKER_KEY, ninjaGetDefaultParams(params));
  const moduleDefaultParams = makeObjectMapping(TRACKER_KEY, getDefaultParams(params));

  // make sure platform field is set AFTER all mappings
  customParams = applyPlatform(customParams);

  // eventName, (trackPage) and eventType
  if (params.eventData.category) {
    if (params.eventData.category) {
      eventData.push(params.eventData.category);
    }
    if (params.eventData.action) {
      eventData.push(params.eventData.action);
    }
    if (params.eventData.label) {
      eventData.push(params.eventData.label);
    }
    if (params.eventData.value) {
      eventData.push(params.eventData.value);
    }

    lastEventName = convertString(eventData.join('_'));
    newValues = makeMapping(TRACKER_KEY, 'trackEvent', lastEventName);
    if (newValues.key === false || newValues.value === false) {
      return {};
    }

    // eN and rT are critical params and we do not check them against the matrix
    lastEventName = newValues.value;
    urlParams.eN = lastEventName;

    if (hydraConfig.triggeredEvents.includes(lastEventName)) {
      urlParams.rT = 1;
    }
  }

  if (params.pageName) {
    // Add original trackPage
    newValues = makeMapping(TRACKER_KEY, 'trackPage', params.pageName);
    if (newValues.key === false || newValues.value === false) {
      return {};
    }

    // eN and rT are critical params and we do not check them against the matrix
    lastTrackPage = newValues.value;

    if (!firstTrackPage) {
      firstTrackPage = lastTrackPage;
    }

    urlParams.eN = lastTrackPage;

    if (hydraConfig.triggeredEvents.includes(lastTrackPage)) {
      urlParams.rT = 1;
    }
  }

  // Order matters! - GET requests can get truncated for being too long.
  // Make sure the default params are always upfront
  // Encode shared default params first
  for (const [key, value] of Object.entries(ninjaDefaultParams)) {
    urlParams[key] = value;
  }

  // Encode tracker default params second
  for (const [key, value] of Object.entries(moduleDefaultParams)) {
    urlParams[key] = value;
  }

  // Encode custom params. Check first for name clash with default params
  for (const [key, value] of Object.entries(customParams)) {
    if (!moduleDefaultParams[key] && !ninjaDefaultParams[key]) {
      if (key !== 'extra') {
        urlParams[key] = value;
      } else {
        urlParams[key] = JSON.stringify(value);
      }
    }
  }

  return urlParams;
}

// Internal function - Translate pageName
function getPageName() {
  return getCurrentPath();
}

function getCustomParams(params) {
  const customParams = {};
  let newValues;

  // Set custom values with unknown name
  for (const [key, value] of Object.entries(params.customParams)) {
    // Transform all
    newValues = makeMapping(TRACKER_KEY, key, value);
    // console.debug(newValues);
    if (newValues.key === false) {
      continue;
    }

    if (newValues.key === 'trackEvent') {
      customParams[newValues.key] = newValues.value;
    } else if (['impressions', 'adImpressions', 'ad_impressions'].includes(newValues.key)) {
      // TODO: Remove this with the bad ninja
      customParams[newValues.key] = JSON.stringify(newValues.value);
    } else if (hydraConfig.knownParameters.includes(newValues.key)) {
      if (typeof newValues.value !== 'object') {
        customParams[newValues.key] = deucex(newValues.value);
        if (customParams[newValues.key] === '' && typeof newValues.value === 'string') {
          customParams[newValues.key] = deucex(newValues.value.replace(/%/g, ''));
        }
      } else {
        customParams[newValues.key] = JSON.stringify(newValues.value);
      }
    } else {
      if (!hydraConfig.ignoreParams.includes(newValues.key)) {
        // Add to extra all the unknown variables
        if (!customParams.extra) {
          customParams.extra = {};
        }
        if (typeof newValues.value !== 'object') {
          if (
            typeof newValues.value === 'string' && // Is an string
            newValues.value.length > 6 && // With more than 6 chars
            newValues.value.slice(0, 3) === '%5B' && // The first 3 are a '[' encoded
            newValues.value.slice(-3) === '%5D' // The last 3 are a ']' encoded
          ) {
            // Remove the encoded '[' and ']'
            // Remove the encoded '"' and "'"
            // Split by the encoded ',' to generate an array
            // Send it as array
            customParams.extra[newValues.key] = newValues.value
              .replace(/%5B/g, '')
              .replace(/%5D/g, '')
              .replace(/%22/g, '')
              .replace(/%27/g, '')
              .split('%2C');
          } else {
            customParams.extra[newValues.key] = newValues.value;
          }
        } else {
          customParams.extra[newValues.key] = newValues.value;
        }
      }
    }

    // console.log(newValues.key, newValues.value, '-->', customParams[newValues.key]);
  }

  return customParams;
}

/**
 * @deprecated Use BeaconAPI version `trackWithBeacon`
 */
function trackWithImage(src) {
  if (!getNinjaConfig().unitTest) {
    const hydraImage = new Image();
    hydraImage.onload = () => {
      onHitSentOk();
      hydraImage.onload = null;
      hydraImage.onerror = null;
    };
    hydraImage.onerror = () => {
      trackError('HIT_FAIL', 'Hydra', 'trackWithImage', src);
      hydraImage.onload = null;
      hydraImage.onerror = null;
    };
    hydraImage.src = src;
  }
}

// Internal function - Callback after finish one hit
function onHitSentOk() {
  if (isLinkEvent) {
    collectCalls();
    isLinkEvent = false;
  }
}

export function convertString(text) {
  if (typeof text === 'string') {
    const tmp = text.toLowerCase().replace(/[^a-z0-9\/_]/g, '-');

    return tmp.replace(/\s{2,}/g, ' ').replace(/-{2,}/g, '-');
  }
  return text;
}

export function cleanup() {
  const usedCookies = ['onap', 'lqonap', 'laquesis', 'laquesisff', 'laquesissu', 'lqstatus', 'laquesisqa'];

  usedCookies.forEach(cleanCookie);
}

// Entry function - Track a page
export function trackPage(params) {
  if (!hasRequiredConsentFor(Trackers.Hydra)) {
    cleanup();
    return;
  }

  init();
  const urlParams = getParams(params);
  const streamURL = getProtocol() + HYDRA_HOST + hydraConfig.path;

  if (Object.keys(urlParams).length === 0) {
    onHitSentOk();
  } else if (getNinjaConfig().useBeaconAPI) {
    trackWithBeacon(streamURL, urlParams).finally(() => onHitSentOk());
  } else {
    trackWithImage(`${streamURL}?${objectToQueryString(urlParams)}`);
  }

  /**
   * load vitals after first page event. It doesn't matter when the library is loaded as
   * the metrics are stored by the browser and are available at any time.
   * There's a check inside for browser compatibility and load the library only once
   */
  if (hydraConfig.vitals && hydraConfig.vitals_path && firstTrackPage) {
    loadWebVitals(
      /**
       * @param {PerformanceMetric} metric
       */
      function (metric) {
        // console.log(metric.name, firstTrackPage, metric);
        trackWebVital(metric, firstTrackPage, applyPlatform);
      }
    );
  }
  // finished = true;
}

// Entry function - Track an event
export function trackEvent(params) {
  // finished = false;
  trackPage(params);
  // finished = true;
}

// Entry function - Track a link event
export function trackLinkEvent(params) {
  isLinkEvent = true;
  trackEvent(params);
}

export function trackPerformanceEvent(params) {
  if (!hasRequiredConsentFor(Trackers.Hydra)) {
    cleanup();
    return;
  }

  if (!window.JSON || !hydraConfig.vitals_path || !(navigator.sendBeacon || window.fetch)) {
    return;
  }

  init();
  const urlParams = getParams(params);
  const url = getProtocol() + HYDRA_HOST + hydraConfig.vitals_path;

  trackWithBeacon(url, urlParams);
}

export function trackFeatureUsage(params) {
  if (!hasRequiredConsentFor(Trackers.Hydra)) {
    cleanup();
    return;
  }

  if (!window.JSON || !hydraConfig.vitals_path || !(navigator.sendBeacon || window.fetch)) {
    return;
  }

  init();
  const urlParams = {
    ...getParams(params),
    eN: 'ninja_debug',
    message: params.customParams?.message,
    is_native: getNinjaConfig().isNative,
    git_tag: process.env.__GIT_TAG__,
    git_revision: process.env.__GIT_HASH__,
    git_branch: process.env.__GIT_BRANCH__,
  };
  const url = getProtocol() + HYDRA_HOST + 'eu-ninja-debug';

  trackWithBeacon(url, urlParams);
}

export function getIdentifier() {
  return getNinjaConfig().currentSessionLong;
}
